import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, Phone, MapPin, Globe, X, Check, Loader2, Menu, VolumeX, Wind, Droplets, Lock, Thermometer, Minus, Gauge, Sparkles, Instagram } from "lucide-react";
import { Toaster, toast } from "sonner";
import { PRODUCTS, PRODUCT_BY_CODE } from "@/lib/products";
import { HOME } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SITE = {
  domain: "solissindustries.com",
  email: "sales@solissindustries.com",
  phone: "+91 73878 22333",
  address: "Pisoli, Pune – 411060, Maharashtra, India",
  instagram: "solissindustries",
  instagramUrl: "https://www.instagram.com/solissindustries/",
};

/* ---------------- Inquiry Modal ---------------- */
function InquiryDialog({ open, onClose, productCode }) {
  const product = productCode ? PRODUCT_BY_CODE[productCode] : null;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    project_type: "residential",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setForm((f) => ({
        ...f,
        message: product
          ? `I'd like a quote and consultation for the ${product.name} (${product.code}).`
          : f.message,
      }));
    }
  }, [open, product]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        product_code: productCode || null,
      });
      setSuccess(true);
      toast.success("Inquiry sent. We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", location: "", project_type: "residential", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={HOME.inquiryDialog}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-[#0a0a0a]/70 backdrop-blur-sm p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#f1f4f9] text-[#0a0e1a] w-full sm:max-w-2xl sm:rounded-sm shadow-2xl relative overflow-hidden"
          >
            <button
              data-testid={HOME.inquiryClose}
              onClick={onClose}
              className="absolute top-5 right-5 z-10 p-2 hover:bg-[#0a0e1a]/5 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {success ? (
              <div data-testid={HOME.inquirySuccess} className="px-8 sm:px-12 py-16 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#0a0e1a] text-[#f1f4f9] flex items-center justify-center mb-6">
                  <Check size={22} />
                </div>
                <h3 className="font-serif text-3xl mb-3">Your inquiry is with us.</h3>
                <p className="text-[#0a0e1a]/70 max-w-md mx-auto leading-relaxed">
                  A specialist from the Soliss studio will reach out within one business day to discuss
                  measurements, finishes and lead time.
                </p>
                <button
                  onClick={onClose}
                  className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase border-b border-[#0a0e1a] pb-1 hover:gap-3 transition-all"
                >
                  Continue browsing
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 sm:px-12 py-10 sm:py-12">
                <div className="mb-8">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#0a0e1a]/50 mb-3">
                    Customer Inquiry
                  </p>
                  <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
                    Tell us about your project.
                  </h2>
                  {product && (
                    <p
                      data-testid={HOME.inquiryProductLabel}
                      className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#1A6FE8] bg-[#2BB4F2]/10 border border-[#2BB4F2]/40 px-3 py-1.5"
                    >
                      Re: {product.code} · {product.name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                  <Field label="Full name" required>
                    <input
                      data-testid={HOME.inquiryName}
                      required
                      minLength={2}
                      value={form.name}
                      onChange={update("name")}
                      className={inputCls}
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid={HOME.inquiryEmail}
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputCls}
                      placeholder="jane@studio.com"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      data-testid={HOME.inquiryPhone}
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                      placeholder="+1 415 555 0142"
                    />
                  </Field>
                  <Field label="Location">
                    <input
                      data-testid={HOME.inquiryLocation}
                      value={form.location}
                      onChange={update("location")}
                      className={inputCls}
                      placeholder="City, region"
                    />
                  </Field>
                  <Field label="Project type" full>
                    <select
                      data-testid={HOME.inquiryProjectType}
                      value={form.project_type}
                      onChange={update("project_type")}
                      className={inputCls + " appearance-none bg-[#f1f4f9]"}
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="renovation">Renovation</option>
                      <option value="new_build">New build</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Tell us more" required full>
                    <textarea
                      data-testid={HOME.inquiryMessage}
                      required
                      minLength={5}
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      className={inputCls + " resize-none"}
                      placeholder="Opening sizes, finish preferences, timeline…"
                    />
                  </Field>
                </div>

                <button
                  data-testid={HOME.inquirySubmit}
                  type="submit"
                  disabled={submitting}
                  className="mt-8 group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0a0e1a] text-[#f1f4f9] px-8 py-4 text-sm tracking-[0.2em] uppercase hover:bg-[#2BB4F2] hover:text-[#0a0e1a] transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send Inquiry <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                <p className="mt-4 text-[11px] text-[#0a0e1a]/50">
                  By submitting you agree to be contacted by Soliss Industries about your project.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#0a0e1a]/25 focus:border-[#0a0e1a] focus:outline-none py-2.5 text-[15px] placeholder:text-[#0a0e1a]/30 transition-colors";

function Field({ label, children, required, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-[10px] tracking-[0.25em] uppercase text-[#0a0e1a]/55 mb-1">
        {label}
        {required && <span className="text-[#2BB4F2] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

/* ---------------- Nav ---------------- */
function Nav({ onInquire }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0a0a0a]/85 backdrop-blur-md py-3" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <a
          data-testid={HOME.navLogo}
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <SolissMark />
        </a>

        <nav className="hidden md:flex items-center gap-10 text-[12px] tracking-[0.25em] uppercase text-[#f1f4f9]/80">
          <button data-testid={HOME.navProducts} onClick={() => scrollTo("products")} className="hover:text-[#2BB4F2] transition-colors">
            Collection
          </button>
          <button data-testid={HOME.navAbout} onClick={() => scrollTo("about")} className="hover:text-[#2BB4F2] transition-colors">
            Atelier
          </button>
          <button data-testid={HOME.navContact} onClick={() => scrollTo("contact")} className="hover:text-[#2BB4F2] transition-colors">
            Contact
          </button>
        </nav>

        <button
          data-testid={HOME.navInquiryBtn}
          onClick={() => onInquire(null)}
          className="hidden md:inline-flex items-center gap-2 bg-[#2BB4F2] text-[#0a0e1a] px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#f1f4f9] transition-colors"
        >
          Request a quote <ArrowUpRight size={14} />
        </button>

        <button onClick={() => setOpen(!open)} className="md:hidden text-[#f1f4f9] p-2" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-[#0a0a0a] border-t border-[#2BB4F2]/20 px-6 py-6 space-y-5"
          >
            <button onClick={() => scrollTo("products")} className="block text-[#f1f4f9] text-sm tracking-[0.25em] uppercase">Collection</button>
            <button onClick={() => scrollTo("about")} className="block text-[#f1f4f9] text-sm tracking-[0.25em] uppercase">Atelier</button>
            <button onClick={() => scrollTo("contact")} className="block text-[#f1f4f9] text-sm tracking-[0.25em] uppercase">Contact</button>
            <button onClick={() => { setOpen(false); onInquire(null); }} className="w-full bg-[#2BB4F2] text-[#0a0e1a] py-3 text-sm tracking-[0.25em] uppercase">
              Request a quote
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function SolissMark({ variant = "dark" }) {
  // variant "dark" = on dark bg, "light" = on light bg
  return (
    <img
      src="/soliss-logo.png"
      alt="Soliss Industries"
      className={`h-8 sm:h-10 w-auto select-none ${variant === "light" ? "" : ""}`}
      draggable={false}
    />
  );
}

/* ---------------- Bespoke artwork ---------------- */
function BespokeArtwork({ code }) {
  return (
    <div
      data-testid={HOME.productImage(code)}
      className="w-full h-full relative flex items-center justify-center overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 30%, #1A6FE8 0%, #0a0e1a 55%, #000 100%)",
      }}
    >
      <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 400 500" preserveAspectRatio="none">
        {Array.from({ length: 14 }).map((_, i) => (
          <path
            key={i}
            d={`M -50 ${50 + i * 35} Q 200 ${i * 28} 450 ${100 + i * 32}`}
            fill="none"
            stroke="#2BB4F2"
            strokeWidth="0.6"
            opacity={0.5 + (i % 4) * 0.1}
          />
        ))}
      </svg>
      <div className="relative text-center px-8">
        <div className="font-serif italic text-[#f1f4f9] text-3xl sm:text-4xl leading-tight mb-3">
          Drawn to plan.
        </div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#2BB4F2]">
          As per design
        </div>
      </div>
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero({ onInquire }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-[#0a0a0a]">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src="/products/sol-s23-slimline.jpg"
          alt="Soliss S23 Slimline sliding wall overlooking the mountains"
          className="w-full h-full object-cover opacity-90 ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000]/85 via-[#000]/55 to-[#000]/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#000]/30 via-transparent to-[#000]" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 pb-32 min-h-screen flex flex-col justify-end">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-[#2BB4F2] text-[11px] tracking-[0.4em] uppercase mb-6"
        >
          Aluminium & uPVC windows · doors · since 2014
        </motion.p>

        <motion.h1
          data-testid={HOME.heroHeadline}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9 }}
          className="font-serif text-[#f1f4f9] text-[44px] sm:text-[72px] lg:text-[104px] leading-[0.95] tracking-[-0.02em] max-w-5xl"
        >
          Framed light.
          <br />
          <span className="text-[#2BB4F2] italic font-light">Unframed view.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.8 }}
          className="text-[#f1f4f9]/70 text-base sm:text-lg max-w-xl mt-8 leading-relaxed"
        >
          Soliss Industries designs and manufactures aluminium and premium uPVC fenestration for
          residences and buildings where the architecture is meant to outlast its decade.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid={HOME.heroCta}
            onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-3 bg-[#f1f4f9] text-[#0a0e1a] px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#2BB4F2] transition-colors"
          >
            See the collection
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => onInquire(null)}
            className="group inline-flex items-center gap-3 text-[#f1f4f9] px-2 py-4 text-[11px] tracking-[0.3em] uppercase border-b border-[#f1f4f9]/40 hover:border-[#2BB4F2] hover:text-[#2BB4F2] transition-colors"
          >
            Speak to a specialist
          </button>
        </motion.div>

        <motion.div
          data-testid={HOME.heroScrollHint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="hidden md:flex absolute bottom-10 right-12 items-center gap-3 text-[#f1f4f9]/40 text-[10px] tracking-[0.4em] uppercase"
        >
          <span className="w-12 h-px bg-[#f1f4f9]/40" />
          Scroll
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-6 lg:left-12 flex items-center gap-3"
        >
          <span className="text-[9px] tracking-[0.35em] uppercase text-[#f1f4f9]/45">
            In assoc.
          </span>
          <img
            src="/alcoi-logo.png"
            alt="ALCOI India Pvt Ltd"
            className="h-6 w-auto opacity-70"
          />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Marquee — moving product strip ---------------- */
function ProductMarquee({ onInquire }) {
  // duplicate the array so the loop reads as continuous
  const strip = [...PRODUCTS, ...PRODUCTS];
  return (
    <section
      aria-label="Product gallery in motion"
      className="bg-[#000] py-12 sm:py-16 overflow-hidden border-y border-[#2BB4F2]/15"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-6 sm:mb-8 flex items-end justify-between gap-6">
        <div>
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#2BB4F2] mb-2">
            In motion · live gallery
          </p>
          <h2 className="font-serif text-xl sm:text-3xl text-[#f1f4f9] leading-tight">
            Every system, in one continuous line.
          </h2>
        </div>
        <span className="hidden md:block text-[10px] tracking-[0.3em] uppercase text-[#f1f4f9]/40 flex-shrink-0">
          Hover to pause
        </span>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-10 sm:w-20 bg-gradient-to-r from-[#000] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-10 sm:w-20 bg-gradient-to-l from-[#000] to-transparent z-10 pointer-events-none" />

        <div className="flex marquee-track gap-0" style={{ width: "max-content" }}>
          {strip.map((p, i) => (
            <button
              key={`${p.code}-${i}`}
              onClick={() => onInquire(p.code)}
              className="relative w-[260px] sm:w-[360px] aspect-[3/4] flex-shrink-0 overflow-hidden bg-[#0a0e1a] group border-r border-[#2BB4F2]/15"
              aria-label={`Inquire about ${p.name}`}
            >
              {p.image ? (
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover object-center ken-burns"
                  loading="lazy"
                />
              ) : (
                <BespokeArtwork code={p.code} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#000]/85 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase text-[#2BB4F2] bg-[#000]/70 px-2.5 py-1">
                {p.code}
              </div>
              <div className="absolute bottom-5 left-5 right-5 text-left">
                <div className="font-serif text-[#f1f4f9] text-xl leading-tight drop-shadow-lg">
                  {p.name}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#f1f4f9]/70 mt-1">
                  {p.category}
                </div>
              </div>
              <div className="absolute inset-0 ring-0 ring-inset ring-[#2BB4F2] group-hover:ring-2 transition-all duration-300" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Products ---------------- */
function Products({ onInquire }) {
  return (
    <section
      id="products"
      data-testid={HOME.productsSection}
      className="bg-[#f1f4f9] text-[#0a0e1a] py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 sm:mb-24">
          <div className="lg:col-span-4">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#2BB4F2] mb-4">The Collection</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-[-0.01em]">
              Ten systems.
              <br />
              <span className="italic font-light text-[#0a0e1a]/70">One philosophy.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-[#0a0e1a]/70 text-base sm:text-lg leading-relaxed">
              Each Soliss system is engineered around a single conviction — that the frame should
              serve the view, not compete with it. Made from thermally broken aluminium, finished
              by hand at our atelier, and installed by a team trained on the systems they fit.
            </p>
          </div>
        </div>

        <div className="space-y-24 sm:space-y-32">
          {PRODUCTS.map((p, i) => (
            <ProductRow key={p.code} product={p} index={i} onInquire={onInquire} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductRow({ product, index, onInquire }) {
  const flip = index % 2 === 1;
  return (
    <motion.article
      data-testid={HOME.productCard(product.code)}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center`}
    >
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden bg-[#0a0e1a]/5 group shimmer" style={{ "--shimmer-delay": `${index * 0.7}s` }}>
          {product.image ? (
            <img
              data-testid={HOME.productImage(product.code)}
              src={product.image}
              alt={`${product.name} – ${product.category}`}
              className={`w-full h-full object-cover ${index % 2 === 0 ? "ken-burns" : "ken-burns ken-burns-2"}`}
              loading="lazy"
            />
          ) : (
            <BespokeArtwork code={product.code} />
          )}
          <div className="absolute top-5 left-5 bg-[#0a0a0a]/85 text-[#f1f4f9] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase z-10">
            {product.code}
          </div>
          {product.bespoke && (
            <div className="absolute top-5 right-5 bg-[#1A6FE8] text-[#f1f4f9] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase z-10">
              Bespoke
            </div>
          )}
        </div>
      </div>

      <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <p className="text-[10px] tracking-[0.35em] uppercase text-[#2BB4F2] mb-3">
          {product.category}
        </p>
        <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.01em] mb-5">
          {product.name}
        </h3>
        <p className="font-serif italic text-xl text-[#0a0e1a]/70 mb-6">{product.tagline}</p>
        <p className="text-[#0a0e1a]/80 leading-relaxed mb-8">{product.description}</p>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8 border-t border-[#0a0e1a]/15 pt-6">
          {product.specs.map((s) => (
            <div key={s.label}>
              <dt className="text-[10px] tracking-[0.25em] uppercase text-[#0a0e1a]/50 mb-1">
                {s.label}
              </dt>
              <dd className="text-[14px]">{s.value}</dd>
            </div>
          ))}
        </dl>

        <ul className="space-y-2 mb-10">
          {product.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-[#0a0e1a]/75">
              <span className="mt-2 w-1 h-1 bg-[#2BB4F2] flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        <button
          data-testid={HOME.productInquireBtn(product.code)}
          onClick={() => onInquire(product.code)}
          className="group inline-flex items-center gap-3 bg-[#0a0e1a] text-[#f1f4f9] px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#2BB4F2] hover:text-[#0a0e1a] transition-colors"
        >
          Inquire about {product.code}
          <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform duration-300" />
        </button>
      </div>
    </motion.article>
  );
}

/* ---------------- System Aluminium Benefits ---------------- */
const BENEFITS = [
  {
    icon: VolumeX,
    title: "Sound-proof",
    body: "Multi-chamber sections and acoustic gaskets that hush a busy street to a hum.",
  },
  {
    icon: Wind,
    title: "Dust-proof",
    body: "Triple-lip seals and brush gaskets keep airborne grit on the outside of your home.",
  },
  {
    icon: Gauge,
    title: "Air-tight",
    body: "Tested to Class 4 air permeability — no draughts, no whistling sashes, no leaks.",
  },
  {
    icon: Droplets,
    title: "Water-tight",
    body: "Class 9A weather sealing with concealed drainage that handles monsoon-grade rainfall.",
  },
  {
    icon: Lock,
    title: "Multi-point locking",
    body: "Espagnolette gearing engages locking points around the entire frame, not just at the handle.",
  },
  {
    icon: Thermometer,
    title: "Thermal breaks",
    body: "Polyamide insulators inside every aluminium section — keeping heat outside in summer and inside in winter.",
  },
  {
    icon: Minus,
    title: "Flush thresholds",
    body: "Near-flat sills (as low as 18 mm) for seamless indoor–outdoor flow and a barrier-free home.",
  },
  {
    icon: Sparkles,
    title: "Maintenance-free",
    body: "Anodised aluminium and uPVC sections that never need painting, sanding or sealing — wipe with a damp cloth, that's it.",
  },
];

function SystemBenefits() {
  return (
    <section className="bg-[#f1f4f9] text-[#0a0e1a] py-24 sm:py-32 border-t border-[#0a0e1a]/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#1A6FE8] mb-4">
              System aluminium · eight engineered benefits
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-[-0.01em]">
              Built quietly.
              <br />
              <span className="italic font-light text-[#0a0e1a]/70">Sealed completely.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-3">
            <p className="text-[#0a0e1a]/70 text-base sm:text-lg leading-relaxed">
              Every Soliss system aluminium frame is engineered against the same eight benchmarks —
              the small details that decide whether a window simply closes, or actually performs
              for the next twenty years.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0a0e1a]/10 border border-[#0a0e1a]/10">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#f1f4f9] p-8 lg:p-10 hover:bg-white transition-colors duration-300 group relative"
              >
                <div className="w-12 h-12 rounded-full bg-[#0a0e1a] text-[#2BB4F2] flex items-center justify-center mb-6 group-hover:bg-[#1A6FE8] group-hover:text-[#f1f4f9] transition-colors duration-300">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#1A6FE8] mb-2">
                  0{i + 1}
                </div>
                <h3 className="font-serif text-2xl leading-tight mb-3">{b.title}</h3>
                <p className="text-sm text-[#0a0e1a]/70 leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- About ---------------- */
function About() {
  const stats = [
    { n: "11", l: "Years building" },
    { n: "1,400+", l: "Projects delivered" },
    { n: "0.6", l: "Lowest Ug, W/m²K" },
    { n: "20 yr", l: "System warranty" },
  ];
  return (
    <section id="about" data-testid={HOME.aboutSection} className="bg-[#0a0a0a] text-[#f1f4f9] py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "radial-gradient(#2BB4F2 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#2BB4F2] mb-4">The Atelier</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-[-0.01em]">
            Built quietly.
            <br />
            <span className="italic font-light text-[#f1f4f9]/60">Built to last.</span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-[#f1f4f9]/75 leading-relaxed">
          <p>
            Soliss Industries was founded by a small team of engineers and fabricators who believed
            modern fenestration had drifted from craft toward catalogue. We design every system in
            our atelier, prototype it on our own factory floor, and refuse to ship anything we
            wouldn&apos;t install in our own homes.
          </p>
          <p>
            We work directly with architects, builders and homeowners — no resellers, no distance
            between design and delivery. The systems on this page are the entirety of what we make.
            We&apos;d rather perfect four than ship forty.
          </p>
          <div className="pt-6 border-t border-[#f1f4f9]/15">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#2BB4F2] mb-3">In association with</p>
            <div className="flex items-center gap-5">
              <img
                src="/alcoi-logo.png"
                alt="ALCOI India Pvt Ltd"
                className="h-14 w-auto bg-[#f1f4f9] p-2 rounded-sm"
              />
              <div>
                <div className="font-serif text-xl text-[#f1f4f9]">ALCOI India Pvt Ltd</div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#f1f4f9]/55 mt-1">
                  Manufacturing & engineering partner
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-8 border-t border-[#f1f4f9]/15">
            {stats.map((s) => (
              <div key={s.l}>
                <div className="font-serif text-3xl sm:text-4xl text-[#2BB4F2]">{s.n}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#f1f4f9]/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Contact / Footer ---------------- */
function ContactFooter({ onInquire }) {
  return (
    <footer id="contact" data-testid={HOME.footerSection} className="bg-[#f1f4f9] text-[#0a0e1a]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 sm:pt-32 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20 border-b border-[#0a0e1a]/15">
          <div className="lg:col-span-7">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#2BB4F2] mb-4">Contact</p>
            <h2 className="font-serif text-4xl sm:text-6xl leading-[1.02] tracking-[-0.01em] mb-8">
              Begin a conversation
              <br />
              <span className="italic font-light text-[#0a0e1a]/60">with the atelier.</span>
            </h2>
            <button
              onClick={() => onInquire(null)}
              className="group inline-flex items-center gap-3 bg-[#0a0e1a] text-[#f1f4f9] px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#2BB4F2] hover:text-[#0a0e1a] transition-colors"
            >
              Submit an inquiry <ArrowUpRight size={15} />
            </button>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 space-y-6 text-sm">
            <div className="flex items-start gap-4">
              <Mail size={16} className="mt-0.5 text-[#2BB4F2] flex-shrink-0" />
              <a href={`mailto:${SITE.email}`} className="hover:text-[#2BB4F2] transition-colors">
                {SITE.email}
              </a>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={16} className="mt-0.5 text-[#2BB4F2] flex-shrink-0" />
              <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="hover:text-[#2BB4F2] transition-colors">
                {SITE.phone}
              </a>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={16} className="mt-0.5 text-[#2BB4F2] flex-shrink-0" />
              <span className="whitespace-pre-line">{SITE.address}</span>
            </div>
            <div className="flex items-start gap-4">
              <Globe size={16} className="mt-0.5 text-[#2BB4F2] flex-shrink-0" />
              <a href={`https://www.${SITE.domain}`} className="hover:text-[#2BB4F2] transition-colors">
                www.{SITE.domain}
              </a>
            </div>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="flex items-start gap-4 group"
            >
              <Instagram size={16} className="mt-0.5 text-[#2BB4F2] flex-shrink-0 group-hover:scale-110 transition-transform" />
              <span className="group-hover:text-[#2BB4F2] transition-colors">
                @{SITE.instagram}
              </span>
            </a>
          </div>
        </div>

        <div className="pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 text-[11px] tracking-[0.2em] uppercase text-[#0a0e1a]/55">
          <div className="flex items-center gap-3">
            <SolissMark />
            <span>
              © {new Date().getFullYear()} Soliss Industries ·{" "}
              <a data-testid={HOME.footerDomain} href={`https://${SITE.domain}`} className="hover:text-[#2BB4F2]">
                {SITE.domain}
              </a>
            </span>
          </div>
          <div className="flex gap-6 items-center">
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-2 hover:text-[#2BB4F2] transition-colors group"
              aria-label="Soliss Industries on Instagram"
            >
              <Instagram size={16} className="group-hover:scale-110 transition-transform" />
              <span>@{SITE.instagram}</span>
            </a>
            <span className="hidden sm:inline">In association with</span>
            <img src="/alcoi-logo.png" alt="ALCOI India Pvt Ltd" className="h-8 w-auto" />
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Page ---------------- */
const Home = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeCode, setActiveCode] = useState(null);

  useEffect(() => {
    // ping backend
    axios.get(`${API}/`).catch(() => {});
  }, []);

  const openInquiry = (code) => {
    setActiveCode(code);
    setDialogOpen(true);
  };

  return (
    <div className="bg-[#0a0a0a]">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: "#0a0e1a", color: "#f1f4f9", border: "1px solid #2BB4F2" },
        }}
      />
      <Nav onInquire={openInquiry} />
      <Hero onInquire={openInquiry} />
      <ProductMarquee onInquire={openInquiry} />
      <Products onInquire={openInquiry} />
      <SystemBenefits />
      <About />
      <ContactFooter onInquire={openInquiry} />
      <InquiryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        productCode={activeCode}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
